/**
 * VendorLedgerPage — Admin Vendor Ledger Dashboard
 *
 * Calls:
 *   GET /marketplace/vendors                   → vendor list (for selector)
 *   GET /marketplace/vendors/:id/ledger        → ledger entries + summary
 *   GET /marketplace/vendors/:id/payments      → payment history
 *
 * Aggregates:
 *   - Total paid
 *   - Total pending
 *
 * Displays:
 *   - Vendor selector
 *   - Summary cards
 *   - Transaction history with date filters
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  MenuBook,
  Search,
  FilterList,
  Refresh,
  Download,
  ArrowUpward,
  ArrowDownward,
  AccountBalanceWallet,
  Receipt,
  Storefront,
} from '@mui/icons-material';
import { marketplaceAPI } from 'services/api';

// ─── Helpers ────────────────────────────────────────────────
const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  const n = parseFloat(amount);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const fmtFull = (amount) =>
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const formatDate = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ═════════════════════════════════════════════════════════════
const VendorLedgerPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Vendors list for selector
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');

  // Ledger data for selected vendor
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ── Load Vendors ───────────────────────────────────────────
  const loadVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await marketplaceAPI.getVendors();
      const list = Array.isArray(res.data) ? res.data : [];
      setVendors(list);
      // Auto-select first vendor if available
      if (list.length > 0 && !selectedVendorId) {
        setSelectedVendorId(list[0].id);
      }
    } catch (err) {
      console.error('VendorLedger vendors load error:', err);
      setError('Failed to load vendor list.');
    } finally {
      setLoading(false);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // ── Load Ledger for Selected Vendor ────────────────────────
  const loadLedger = useCallback(async (vendorId, isRefresh = false) => {
    if (!vendorId) return;
    try {
      if (isRefresh) setRefreshing(true);
      else setLedgerLoading(true);

      const [ledgerRes, paymentsRes] = await Promise.allSettled([
        marketplaceAPI.getVendorLedger(vendorId),
        marketplaceAPI.getVendorPayments(vendorId),
      ]);

      if (ledgerRes.status === 'fulfilled') {
        setLedgerSummary(ledgerRes.value.data || null);
      } else {
        setLedgerSummary(null);
      }

      if (paymentsRes.status === 'fulfilled') {
        setPayments(Array.isArray(paymentsRes.value.data) ? paymentsRes.value.data : []);
      } else {
        setPayments([]);
      }
    } catch (err) {
      console.error('VendorLedger data load error:', err);
    } finally {
      setLedgerLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (selectedVendorId) {
      loadLedger(selectedVendorId);
    }
  }, [selectedVendorId, loadLedger]);

  // ── Selected Vendor ────────────────────────────────────────
  const selectedVendor = vendors.find((v) => v.id === selectedVendorId);

  // ── Derived Aggregates ─────────────────────────────────────
  const totalPaid = useMemo(
    () =>
      payments
        .filter((p) => (p.status || '').toLowerCase() === 'paid')
        .reduce((s, p) => s + (parseFloat(p.net_amount) || parseFloat(p.amount) || 0), 0),
    [payments]
  );

  const totalPending = useMemo(
    () =>
      payments
        .filter((p) => (p.status || '').toLowerCase() === 'pending')
        .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0),
    [payments]
  );

  const totalCommission = useMemo(
    () => payments.reduce((s, p) => s + (parseFloat(p.commission_amount) || 0), 0),
    [payments]
  );

  // ── Filter Payments (search + date) ────────────────────────
  const filteredPayments = useMemo(() => {
    return payments.filter((p) => {
      const matchSearch =
        !search ||
        (p.payment_number || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.transaction_reference || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.id?.toString() || '').includes(search);

      let matchDate = true;
      if (dateFrom) {
        const pDate = new Date(p.payment_date || 0);
        matchDate = matchDate && pDate >= new Date(dateFrom);
      }
      if (dateTo) {
        const pDate = new Date(p.payment_date || 0);
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        matchDate = matchDate && pDate <= end;
      }

      return matchSearch && matchDate;
    });
  }, [payments, search, dateFrom, dateTo]);

  // ── Filter Ledger Entries (search + date) ──────────────────
  const ledgerEntries = ledgerSummary?.entries || [];
  const filteredLedgerEntries = useMemo(() => {
    return ledgerEntries.filter((e) => {
      const matchSearch =
        !search ||
        (e.reference_id || '').toLowerCase().includes(search.toLowerCase()) ||
        (e.entry_type || '').toLowerCase().includes(search.toLowerCase());

      let matchDate = true;
      if (dateFrom) {
        const eDate = new Date(e.created_at || 0);
        matchDate = matchDate && eDate >= new Date(dateFrom);
      }
      if (dateTo) {
        const eDate = new Date(e.created_at || 0);
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        matchDate = matchDate && eDate <= end;
      }

      return matchSearch && matchDate;
    });
  }, [ledgerEntries, search, dateFrom, dateTo]);

  // ── Export CSV ──────────────────────────────────────────────
  const exportCSV = () => {
    const header = 'Payment ID,Payment #,Date,Status,Gross Amount,Commission,Net Amount,Transaction Ref\n';
    const rows = filteredPayments
      .map(
        (p) =>
          `${p.id},${p.payment_number},${formatDate(p.payment_date)},${p.status},${parseFloat(p.amount) || 0},${parseFloat(p.commission_amount) || 0},${parseFloat(p.net_amount) || 0},${p.transaction_reference || ''}`
      )
      .join('\n');
    const vendorName = selectedVendor ? selectedVendor.business_name.replace(/\s+/g, '_') : 'unknown';
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor_ledger_${vendorName}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
            <MenuBook sx={{ fontSize: 22, color: '#0d9488' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Ledger</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#0d9488' }} />
          <span className="ml-3 text-sm text-gray-500">Loading vendor data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <MenuBook sx={{ fontSize: 22, color: '#0d9488' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Vendor Ledger</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Track vendor payments, invoices, and outstanding balances
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => {
              if (selectedVendorId) loadLedger(selectedVendorId, true);
            }}
            disabled={refreshing || !selectedVendorId}
            sx={{ backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' } }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} action={<Button size="small" onClick={loadVendors}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* ── Vendor Selector ── */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Storefront sx={{ fontSize: 20, color: '#0d9488' }} />
            <span className="text-sm font-semibold text-gray-700">Select Vendor</span>
          </div>

          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Vendor</InputLabel>
            <Select
              value={selectedVendorId}
              label="Vendor"
              onChange={(e) => setSelectedVendorId(e.target.value)}
            >
              {vendors.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{v.business_name}</span>
                    <span className="text-xs text-gray-400 font-mono">{v.vendor_code}</span>
                    {v.verified && <span className="text-xs text-emerald-500">✓</span>}
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedVendor && (
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>{selectedVendor.city || ''}</span>
              <span>•</span>
              <span>{selectedVendor.phone || ''}</span>
              <Chip
                label={(selectedVendor.status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                size="small"
                variant="outlined"
                color={(selectedVendor.status || '').toLowerCase() === 'active' ? 'success' : 'default'}
                sx={{ fontSize: '0.6rem', height: 20 }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Show content only when a vendor is selected */}
      {!selectedVendorId ? (
        <div className="text-center py-20 text-gray-400">
          <AccountBalanceWallet sx={{ fontSize: 48, mb: 2, color: '#d1d5db' }} />
          <p>Select a vendor above to view their ledger</p>
        </div>
      ) : ledgerLoading ? (
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#0d9488' }} />
          <span className="ml-3 text-sm text-gray-500">Loading ledger data...</span>
        </div>
      ) : (
        <>
          {/* ── Summary Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
              <p className="text-xs text-emerald-600 font-medium mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-emerald-800">{formatCurrency(totalPaid)}</p>
              <p className="text-xs text-emerald-500 mt-1">
                {payments.filter((p) => (p.status || '').toLowerCase() === 'paid').length} transactions
              </p>
            </div>
            <div className="rounded-2xl bg-amber-50 border border-amber-100 p-5">
              <p className="text-xs text-amber-600 font-medium mb-1">Total Pending</p>
              <p className="text-2xl font-bold text-amber-800">{formatCurrency(totalPending)}</p>
              <p className="text-xs text-amber-500 mt-1">
                {payments.filter((p) => (p.status || '').toLowerCase() === 'pending').length} pending
              </p>
            </div>
            <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-5">
              <p className="text-xs text-indigo-600 font-medium mb-1">Commission Earned</p>
              <p className="text-2xl font-bold text-indigo-800">{formatCurrency(totalCommission)}</p>
              <p className="text-xs text-indigo-500 mt-1">Platform commission</p>
            </div>
            <div className="rounded-2xl bg-teal-50 border border-teal-100 p-5">
              <p className="text-xs text-teal-600 font-medium mb-1">Ledger Entries</p>
              <p className="text-2xl font-bold text-teal-800">{ledgerSummary?.total_payments || 0}</p>
              <p className="text-xs text-teal-500 mt-1">
                {ledgerSummary ? `Debits: ${formatCurrency(ledgerSummary.total_debit)}` : 'No entries'}
              </p>
            </div>
          </div>

          {/* ── Payment History ── */}
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden mb-6">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Payment History</h3>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <TextField
                  placeholder="Search by payment # or reference..."
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 260 }}
                />

                <div className="flex items-center gap-2">
                  <FilterList sx={{ fontSize: 16, color: '#9ca3af' }} />
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />
                  {(dateFrom || dateTo) && (
                    <Button
                      size="small"
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                    >
                      Clear
                    </Button>
                  )}
                </div>

                <div className="ml-auto">
                  <Tooltip title="Export CSV">
                    <IconButton size="small" onClick={exportCSV}>
                      <Download sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Showing {filteredPayments.length} of {payments.length} payments
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment #</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Gross</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Commission</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Net Amount</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-gray-400 text-sm">
                        {payments.length === 0
                          ? 'No payments found for this vendor.'
                          : 'No payments match your filters'}
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-gray-600">{p.payment_number}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {formatDate(p.payment_date)}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {fmtFull(p.amount)}
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {parseFloat(p.commission_amount) > 0 ? `−${fmtFull(p.commission_amount)}` : '—'}
                        </td>
                        <td className="px-4 py-3 font-semibold text-emerald-700">
                          {fmtFull(p.net_amount)}
                        </td>
                        <td className="px-4 py-3">
                          <Chip
                            label={(p.status || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            color={(p.status || '').toLowerCase() === 'paid' ? 'success' : 'warning'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
                          />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono truncate max-w-[180px]">
                          {p.transaction_reference || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Escrow Ledger Entries ── */}
          {filteredLedgerEntries.length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700">
                  Escrow Ledger Entries
                  <span className="text-xs text-gray-400 font-normal ml-2">
                    ({filteredLedgerEntries.length} entries)
                  </span>
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Debit</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Credit</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredLedgerEntries.map((e) => (
                      <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">#{e.id}</td>
                        <td className="px-4 py-3">
                          <Chip
                            label={(e.entry_type || '').replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500, fontSize: '0.6rem', height: 22 }}
                          />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{e.debit_account || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{e.credit_account || '—'}</td>
                        <td className="px-4 py-3 font-semibold text-gray-800">{fmtFull(e.amount)}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono truncate max-w-[180px]">
                          {e.reference_id || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(e.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorLedgerPage;
